/**
 * @interface RevisionPermissions
 * @description Permisos del flujo de revisión de materialidad (sección SSO `FlujoRevision`).
 */
export interface RevisionPermissions {
  /** Actividad `VerRevisionMaterialidad`: abre la bandeja "Por revisar" y las acciones del revisor. */
  canViewRevisionMaterialidad: boolean;
  /** Actividad `CerrarRegistro`: cierra un registro en revisión. */
  canCloseRegistro: boolean;
  /** Actividad `DevolverRegistro`: devuelve un registro al responsable con motivo. */
  canReturnRegistro: boolean;
  /** Actividad `RechazarRegistro`: rechaza un registro en revisión o devuelto. */
  canRejectRegistro: boolean;
  /** Actividad `ReabrirRegistro`: reabre un registro cerrado. */
  canReopenRegistro: boolean;
  /** Actividad `EnviarRevisionFactura` (sección ListadoFacturas): el responsable envía o reenvía a revisión. */
  canSendRevisionFactura: boolean;
}

/**
 * @function getRevisionPermissions
 * @description Calcula los permisos del flujo de revisión.
 *
 * El rol Empresa (administrador de la empresa) tiene las acciones del revisor sin
 * depender de la lista; el Operativo solo con la actividad asignada en su rol.
 * Enviar a revisión es exclusivo del Operativo responsable del registro.
 *
 * @param {boolean} isEmpresa - El usuario tiene rol Empresa.
 * @param {boolean} isUser - El usuario tiene rol Operativo.
 * @param {(roleCheck: boolean, actionKey: string) => boolean} canPerformByRoleOrActionList - Helper de actividades.
 * @returns {RevisionPermissions} Permisos del flujo de revisión.
 */
export const getRevisionPermissions = (
  isEmpresa: boolean,
  isUser: boolean,
  canPerformByRoleOrActionList: (roleCheck: boolean, actionKey: string) => boolean
): RevisionPermissions => ({
  canViewRevisionMaterialidad: isEmpresa || canPerformByRoleOrActionList(isUser, 'VerRevisionMaterialidad'),
  canCloseRegistro: isEmpresa || canPerformByRoleOrActionList(isUser, 'CerrarRegistro'),
  canReturnRegistro: isEmpresa || canPerformByRoleOrActionList(isUser, 'DevolverRegistro'),
  canRejectRegistro: isEmpresa || canPerformByRoleOrActionList(isUser, 'RechazarRegistro'),
  canReopenRegistro: isEmpresa || canPerformByRoleOrActionList(isUser, 'ReabrirRegistro'),
  canSendRevisionFactura: canPerformByRoleOrActionList(isUser, 'EnviarRevisionFactura'),
});
